import { Box, Loader, useToast } from 'braid-design-system';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';

import { ChangeStatusButtons } from 'src/App/AppPageBlock/ProfileDrawer/Profile/ChangeStatusButtons';
import {
  type ProfileDrawerPageAnalytics,
  createPageAnalytics,
} from 'src/App/AppPageBlock/ProfileDrawer/createPageAnalytics';
import type { UseHandleStatusChangeResponse } from 'src/App/hooks/useHandleStatusChange';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { JOB_APPLICATION_TAB } from 'src/constants/tabs';
import { STATUS_CHANGE_LOCATION } from 'src/graphql/hirer/updateApplicationsStatus';
import { Display } from 'src/libs/Display';
import type { PageConfig } from 'src/libs/PageConfig';
import { CANDIDATE_PROFILE_DRAWER_TAB_PARAM_KEY } from 'src/pages/CandidateManagementPage/queryParams';
import type { IJobApplication } from 'src/stores/JobApplicationStore/JobApplication';
import type { Bucket } from 'src/stores/JobApplicationStore/types';
import type { IQuestionnaireStore } from 'src/stores/JobStore/QuestionnaireStore';
import { useStores } from 'src/utils/hooks/useStores';

import DetailsContentView from '../DetailsContentView';

import ChangeBucketError from './ChangeBucketError';

import * as styles from './JobApplicationDetails.css';

interface Props {
  jobApplication: IJobApplication;
  onUpdateStatusSuccess: () => Promise<void>;
  applicationCardIsExpanded?: boolean;
  showSuccessMessageToast: boolean;
  handleStatusChange: UseHandleStatusChangeResponse['handleStatusChange'];
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
  userId: string;
  jobId: string;
  seekAdvertiserId: string;
}

export const JobApplicationDetails = observer(
  ({
    jobApplication: { isChangeStateError, isLoading },
    onUpdateStatusSuccess,
    jobApplication,
    showSuccessMessageToast,
    applicationCardIsExpanded,
    handleStatusChange,
    addCustomError,
    userId,
    jobId,
    seekAdvertiserId,
  }: Props) => {
    const store = useStores();
    const questionnaireStore: IQuestionnaireStore =
      store.jobStore.questionnaire;
    const { jobApplicationStore } = store;

    const { locale, environment, site } = useAppConfig();

    const [profileDrawerAnalytics] = useState<ProfileDrawerPageAnalytics>(() =>
      createPageAnalytics({
        site,
        locale,
        environment,
        userId: Number(userId),
        seekAdvertiserId,
      }),
    );

    const [, setTabQueryParam] = useQueryParam(
      CANDIDATE_PROFILE_DRAWER_TAB_PARAM_KEY,
      StringParam,
    );

    const showToast = useToast();

    if (isLoading) {
      return <Loader />;
    }

    const onStatusChange = async (bucket: Bucket) => {
      const statusChangeLocation = STATUS_CHANGE_LOCATION.CANDIDATE_DETAIL;
      await handleStatusChange({
        store,
        jobApplicationStore,
        jobApplication,
        destinationBucket: bucket,
        statusChangeLocation,
        showToast,
        onUpdateSuccess: onUpdateStatusSuccess,
        isExpanded: applicationCardIsExpanded,
        showSuccessMessageToast,
      });
    };

    const changeStatusButtons = (
      <ChangeStatusButtons
        jobApplication={{
          id: jobApplication.id,
          fullName: jobApplication.fullName,
          bucket: jobApplication.bucket,
        }}
        changeStatus={onStatusChange}
      />
    );

    const seekListingId = Number(jobId);
    const candidateId = Number(jobApplication.seekCandidateId);

    return (
      <>
        {isChangeStateError && (
          <Box margin="xsmall">
            <ChangeBucketError />
          </Box>
        )}

        <Box display="flex" flexDirection="column" flexGrow={1}>
          <DetailsContentView
            jobApplication={jobApplication}
            locale={locale}
            questionnaireStore={questionnaireStore}
            jobId={jobId}
            addCustomError={addCustomError}
            changeStatusButtons={changeStatusButtons}
            userId={userId}
            onCloseVerificationsAlert={() => {
              profileDrawerAnalytics.trackLink({
                event: 'alert_dismiss_pressed',
                attributes: {
                  alertShownMessageCode:
                    'Candidate Management - Verified by SEEK Pass Banner',
                  seekListingId,
                  candidateId,
                },
              });
            }}
            onProfileBodyVerificationsAlertMount={() => {
              profileDrawerAnalytics.trackView({
                event: 'candidate_management_banner_displayed',
                attributes: {
                  displayedBanner: 'Career History Verification Banner',
                  seekListingId,
                  candidateId,
                },
              });
            }}
            onClickProfileBodyVerificationsTab={() => {
              setTabQueryParam(JOB_APPLICATION_TAB.VERIFICATIONS);
              profileDrawerAnalytics.trackLink({
                event: 'banner_content_pressed',
                attributes: {
                  bannerElement: 'Go to Verifications Tab',
                  displayedBanner: 'Career History Verification Banner',
                  seekListingId,
                  candidateId,
                },
              });
            }}
            onClickVerificationsAlertLearnMore={() => {
              profileDrawerAnalytics.trackLink({
                event: 'banner_content_pressed',
                attributes: {
                  bannerElement: 'Go to Why Verify SEEK Pass Page',
                  displayedBanner:
                    'Candidate Management - Verified by SEEK Pass Banner',
                  seekListingId,
                  candidateId,
                },
              });
            }}
            onClickVerificationsTabFooterLearnMore={() => {
              profileDrawerAnalytics.trackLink({
                event: 'learn_how_credential_are_verified_pressed',
                attributes: {
                  actionOrigin:
                    'Candidate Management Profile - Verification Tab',
                },
              });
            }}
            onClickProfileTabVerificationsLearnMore={() => {
              profileDrawerAnalytics.trackLink({
                event: 'learn_how_credential_are_verified_pressed',
                attributes: {
                  actionOrigin: 'Candidate Management Profile - Profile Tab',
                },
              });
            }}
            onClickVerificationsTab={() => {
              profileDrawerAnalytics.trackLink({
                event: 'verifications_tab_pressed',
                attributes: { seekListingId, candidateId },
              });
            }}
          />
        </Box>

        <Display mobile>
          <Box
            padding="small"
            textAlign="center"
            className={[styles.fixedToBottom, styles.topShadow]}
          >
            {changeStatusButtons}
          </Box>
        </Display>
      </>
    );
  },
);
